import { Component, OnInit } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { MatSnackBar } from '@angular/material';

const DB_PATH = '/meta/website/cities';

interface WebCity {
  _index: number;      // array index
  city: string;
  displayCity: string;
  slug: string;
  cityPriority: string;
  _editing?: boolean;
  _saving?: boolean;
  _draft?: Partial<WebCity>;
}

@Component({
  selector: 'app-config-website-cities',
  templateUrl: './config-website-cities.component.html',
  styleUrls: ['./config-website-cities.component.scss']
})
export class ConfigWebsiteCitiesComponent implements OnInit {
  cities: WebCity[] = [];
  filtered: WebCity[] = [];
  loading = true;
  quickSearch = '';

  showAddPanel = false;
  adding = false;
  newCity: Partial<WebCity> = this.empty();

  constructor(private db: AngularFireDatabase, private snack: MatSnackBar) {}

  ngOnInit() {
    this.db.database.ref(DB_PATH).once('value', snapshot => {
      const val = snapshot.val();
      if (Array.isArray(val)) {
        this.cities = val
          .filter(v => v != null)
          .map((v, i) => ({ _index: i, city: v.city || '', displayCity: v.displayCity || '', slug: v.slug || '', cityPriority: String(v.cityPriority || '') }))
          .sort((a, b) => Number(a.cityPriority) - Number(b.cityPriority));
      }
      this.loading = false;
      this.applyFilter();
    });
  }

  applyFilter() {
    const q = this.quickSearch.trim().toLowerCase();
    this.filtered = q
      ? this.cities.filter(c =>
          c.city.toLowerCase().includes(q) ||
          c.displayCity.toLowerCase().includes(q) ||
          c.slug.toLowerCase().includes(q))
      : [...this.cities];
  }

  // ── Edit ─────────────────────────────────────────────────────────────────
  startEdit(c: WebCity) {
    c._draft = { city: c.city, displayCity: c.displayCity, slug: c.slug, cityPriority: c.cityPriority };
    c._editing = true;
  }

  cancelEdit(c: WebCity) { c._editing = false; c._draft = undefined; }

  async saveEdit(c: WebCity) {
    c._saving = true;
    const payload = { city: c._draft.city, displayCity: c._draft.displayCity, slug: c._draft.slug, cityPriority: c._draft.cityPriority };
    try {
      await this.db.database.ref(`${DB_PATH}/${c._index}`).update(payload);
      Object.assign(c, payload);
      c._editing = false; c._draft = undefined;
      this.snack.open(`${c.displayCity} updated`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Save failed. Please try again.', 'OK', { duration: 3000 });
    }
    c._saving = false;
  }

  // ── Add ──────────────────────────────────────────────────────────────────
  openAddPanel() { this.newCity = this.empty(); this.showAddPanel = true; }
  cancelAdd() { this.showAddPanel = false; }

  async addCity() {
    if (!this.newCity.city || !this.newCity.slug) {
      this.snack.open('City and Slug are required.', 'OK', { duration: 2500 });
      return;
    }
    this.adding = true;
    const nextIndex = this.cities.length > 0 ? Math.max(...this.cities.map(c => c._index)) + 1 : 0;
    const payload = {
      city: this.newCity.city,
      displayCity: this.newCity.displayCity || this.newCity.city,
      slug: (this.newCity.slug || '').toLowerCase().replace(/\s+/g, '-'),
      cityPriority: String(this.newCity.cityPriority || nextIndex + 1),
    };
    try {
      await this.db.database.ref(`${DB_PATH}/${nextIndex}`).set(payload);
      const entry: WebCity = { _index: nextIndex, ...payload };
      this.cities.push(entry);
      this.cities.sort((a, b) => Number(a.cityPriority) - Number(b.cityPriority));
      this.applyFilter();
      this.showAddPanel = false;
      this.snack.open(`${entry.displayCity} added`, 'OK', { duration: 2500 });
    } catch (e) {
      this.snack.open('Add failed. Please try again.', 'OK', { duration: 3000 });
    }
    this.adding = false;
  }

  private empty(): Partial<WebCity> {
    return { city: '', displayCity: '', slug: '', cityPriority: '' };
  }
}
